import type { Private } from '@seek/indie-api-types';
import {
  Badge,
  ButtonIcon,
  IconDelete,
  TableCell,
  TableRow,
  Text,
  useToast,
} from 'braid-design-system';
import { useState } from 'react';

import { ExpiryDateRenderer } from './ExpiryDateRenderer.tsx';

import { DialogWithMessage } from '#src/components/DialogWithMessage/DialogWithMessage.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';
import { useOrganizations } from '#src/pages/manage/organizations/OrganizationContext.tsx';
import { roleDisplayNames } from '#src/types/users.ts';

interface InvitationRowProps {
  invitation: Private.Auth0OrganizationInvitationPayload;
}

export const InvitationRow = ({ invitation }: InvitationRowProps) => {
  const { permissions } = usePermissions();
  const showToast = useToast();
  const hasWriteAccess = permissions.includes('mutate:users');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const organizations = useOrganizations();

  const deleteInvite = async () => {
    try {
      await organizations.deleteInvite(invitation.id);
      setShowDeleteDialog(false);
    } catch (err) {
      showToast({
        tone: 'critical',
        message: `We couldn’t delete ${name}.`,
        description: String(err),
      });
    }
  };
  return (
    <TableRow key={invitation.id}>
      <TableCell>
        <Text weight="strong">{invitation.email}</Text>
      </TableCell>
      <TableCell>
        <Text>
          <Badge tone="info">{roleDisplayNames[invitation.role]}</Badge>
        </Text>
      </TableCell>
      <TableCell>
        <ExpiryDateRenderer expiryDate={invitation.expiresAt} />
      </TableCell>
      {hasWriteAccess ? (
        <TableCell align="right">
          <ButtonIcon
            id={`${invitation.id}-deleteInvitation-button-icon`}
            label="Delete Invitation"
            icon={<IconDelete />}
            variant="transparent"
            size="large"
            onClick={() => setShowDeleteDialog(true)}
          />
          <DialogWithMessage
            title={`Delete ${invitation.email}?`}
            id={`${invitation.id}-delete`}
            message="Are you sure you want to delete this invitation? This cannot be undone."
            action="Delete"
            showDialog={showDeleteDialog}
            onCancel={() => setShowDeleteDialog(false)}
            onConfirm={deleteInvite}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
};
